import HttpService from './HttpService';
import type { ItemResponse, ItemsResponse } from '@frontend/Models/Http/HttpModels';
import {
  pathJurisdictions,
  pathSiteDeactivate,
  pathSiteId,
  pathSiteReactivate,
  pathSites,
  pathSitesByMatter,
} from '@frontend/__settings__/septSoftwarePaths';

/**
 * Sites and jurisdictions (P6, domain-model-transition).
 *
 * A *site* is where work physically happened, and it is what picks the employment law that
 * governs those hours. Nothing consumes one yet — P6 is build-beside — but P7 requires a site on
 * every matter it classifies, which is why the entry surface lands a phase early.
 */

export interface Jurisdiction {
  id: string;
  code: string;
  name: string;
  country: string;
  region: string;
  currency: string;
  /**
   * The zone a NEW site inherits, not the authority. A jurisdiction is not reliably one zone —
   * BC's Peace River region holds Mountain Standard year-round while the rest of the province
   * moves — so the site's own `timezone` is what decides which local day an hour fell in.
   */
  default_timezone: string;
  is_active: boolean;
}

export interface Site {
  id: string;
  matterid: string;
  name: string;
  jurisdiction_id: string;
  timezone: string;
  is_active: boolean;
  created_at: string | null;
  updated_at: string | null;
}

export interface CreateSiteBody {
  matterid: string;
  name: string;
  jurisdiction_id: string;
  /** Omit to inherit the jurisdiction's zone. The server resolves it; the client never guesses. */
  timezone?: string;
}

export interface UpdateSiteBody {
  name?: string;
  jurisdiction_id?: string;
  timezone?: string;
}

const http = new HttpService();

class SiteService {
  /**
   * The system jurisdiction catalog.
   *
   * `includeInactive` matters for DISPLAY rather than for picking: a site keeps pointing at its
   * jurisdiction after that jurisdiction is retired, and a caller rendering only the active set
   * cannot name the province such a site is in.
   */
  static async getJurisdictions(includeInactive = false): Promise<Jurisdiction[]> {
    const response = await http.get<ItemsResponse<Jurisdiction>>(
      includeInactive ? `${pathJurisdictions}?include_inactive=1` : pathJurisdictions,
    );
    return response.data.items;
  }

  static async getSitesForMatter(matterId: string): Promise<Site[]> {
    const response = await http.get<ItemsResponse<Site>>(pathSitesByMatter(matterId));
    return response.data.items;
  }

  static async createSite(body: CreateSiteBody): Promise<Site> {
    const response = await http.post<ItemResponse<Site>>(pathSites, body);
    return response.data.item;
  }

  static async updateSite(siteId: string, body: UpdateSiteBody): Promise<Site> {
    const response = await http.put<ItemResponse<Site>>(pathSiteId(siteId), body);
    return response.data.item;
  }

  static async deactivateSite(siteId: string): Promise<Site> {
    const response = await http.post<ItemResponse<Site>>(pathSiteDeactivate(siteId), {});
    return response.data.item;
  }

  static async reactivateSite(siteId: string): Promise<Site> {
    const response = await http.post<ItemResponse<Site>>(pathSiteReactivate(siteId), {});
    return response.data.item;
  }

  static async deleteSite(siteId: string): Promise<void> {
    await http.delete(pathSiteId(siteId));
  }
}

export default SiteService;
